// Firestore path → Postgres table resolution for the supabase data plane.
//
// Contract with the backend (do not drift): every Firestore collection maps to
// a table `id text pk, data jsonb, created_at, updated_at`; child collections
// flatten with parent-id columns, filters in path order.
//
// The entity-registry generator also emits a `pg` key into
// entityRegistry.gen.json; when present it's merged over the static map below
// (the static map keeps this file working even before the generator runs).
import registry from './entityRegistry.gen.json';

// key = the collection segments of the path (ids stripped), joined by '/'
const STATIC_MAP = {
  items: { table: 'items', parents: [] },
  'items/assets': { table: 'item_assets', parents: ['item_id'] },
  'items/uploads': { table: 'item_uploads', parents: ['item_id'] },
  dropLinks: { table: 'drop_links', parents: [] },
  jobs: { table: 'jobs', parents: [] },
  'jobs/tasks': { table: 'job_tasks', parents: ['job_id'] },
  jobControls: { table: 'job_controls', parents: [] },
  config: { table: 'config', parents: [] },
  // configs/app is the backend-written status doc → config table, row 'status'
  configs: { table: 'config', parents: [], idMap: { app: 'status' } },
  knowledge: { table: 'knowledge', parents: [] },
  devSessions: { table: 'dev_sessions', parents: [] },
  'devSessions/events': { table: 'dev_session_events', parents: ['session_id'] },
  devJobs: { table: 'dev_jobs', parents: [] },
  'devJobs/logs': { table: 'dev_job_logs', parents: ['job_id'] },
  devAudit: { table: 'dev_audit', parents: [] },
  costs: { table: 'costs', parents: [] },
  devRouting: { table: 'dev_routing', parents: [] },
  allowlist: { table: 'allowlist', parents: [] },
};

function buildMap() {
  const map = { ...STATIC_MAP };
  const pg = registry.pg;
  if (pg && typeof pg === 'object') {
    for (const [rawKey, val] of Object.entries(pg)) {
      if (!val || typeof val.table !== 'string') continue;
      const key = rawKey.replace(/\./g, '/');
      map[key] = {
        table: val.table,
        parents: val.parents || val.parentCols || [],
        ...(val.idMap ? { idMap: val.idMap } : {}),
      };
    }
  }
  return map;
}

const MAP = buildMap();

// resolvePath('items/i1/assets')    → { table: 'item_assets', filters: [{ col: 'item_id', value: 'i1' }] }
// resolvePath('items/i1/assets/a9') → { table: 'item_assets', filters: [{ col: 'item_id', value: 'i1' }], id: 'a9' }
// resolvePath('config/app')         → { table: 'config', filters: [], id: 'app' }
// Odd segment count = collection path (no id); even = doc path (last seg = id).
export function resolvePath(path) {
  const segs = String(path || '').split('/').filter(Boolean);
  if (!segs.length) throw new Error(`[pathMap] empty path '${path}'`);
  const collections = [];
  const ids = [];
  segs.forEach((seg, i) => (i % 2 === 0 ? collections.push(seg) : ids.push(seg)));
  const key = collections.join('/');
  const def = MAP[key];
  if (!def) {
    throw new Error(
      `[pathMap] no Postgres table mapping for path '${path}' (collection key '${key}') — ` +
        'add it to STATIC_MAP in frontend/src/data/pathMap.js or the entity registry.'
    );
  }
  const isDoc = segs.length % 2 === 0;
  const parents = def.parents || [];
  const parentIds = isDoc ? ids.slice(0, -1) : ids;
  if (parentIds.length !== parents.length) {
    throw new Error(
      `[pathMap] path '${path}' carries ${parentIds.length} parent id(s) but table ` +
        `'${def.table}' expects ${parents.length} (${parents.join(', ') || 'none'})`
    );
  }
  const filters = parents.map((col, i) => ({ col, value: parentIds[i] }));
  if (!isDoc) return { table: def.table, filters };
  let id = ids[ids.length - 1];
  if (def.idMap && def.idMap[id]) id = def.idMap[id];
  return { table: def.table, filters, id };
}
